import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import React, { useContext, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';
import { InView } from 'react-intersection-observer';
import { BiLoggerContextProvider } from '@wix/da-bi/pkg/BiLogger.context';
import { BiData } from '@wix/da-bi/pkg/events';
import IconButton from '@wix/da-ds/pkg/Buttons/IconButton';
import CarouselIcon from '@wix/da-ds/pkg/Icons/24x24/Carousel';
import { MeasuredCookieType } from '@wix/da-hooks/pkg/useMeasureElement/redux/types';
import noop from '../../utils/noop';
import { useDeviationViewContext } from '../../Context';
import DuperbrowsePaddles from '../../Duperbrowse/DuperbrowsePaddles';
import EmptyBackground from '../../DeviationBackground/StageBackground/EmptyBackground';
import { SubscriptionPurchaseFTUEContext } from '../../Tier/ftue/SubscriptionPurchaseFTUE/SubscriptionPurchaseFTUEContext';
import SubscriptionPurchaseFTUE from '../../Tier/ftue/SubscriptionPurchaseFTUE/SubscriptionPurchaseFTUE';
import { ADDITIONAL_MEDIA_ID_PREFIX, BI_SCROLL_SECTIONNAME, SCROLL_MODE_THRESHOLD_FOR_INVIEW } from '../constants';
import { useMeasuredStage } from '../hooks/useMeasuredStage';
import { useScrollToCurrentlyViewed } from '../hooks/useScrollToCurrentlyViewed';
import StageContainer from '../_partials/StageContainer';
import ZoomModalFromImageSource from '../_partials/ZoomModalFromImageSource';
import StashPaddles from '../_partials/StashPaddles';
import FullImageSourceWithCaption from '../_partials/FullImageSourceWithCaption';
import { useSelector } from 'react-redux';
import { getIsDuperbrowseActive } from '../../Duperbrowse/redux/selectors';
import styles from './DesktopArtStage.scss';
/** Multi-image deviation that displays as a vertical list of images one above the other */ export const DesktopMultiArtScrollStage = ({ deviation, additionalMedia = [], className, currentlyViewedImageSource, setCurrentlyViewedImageSource = noop, zoomedImageSource, isZoomed, onEnterZoomMode = noop, onExitZoomMode = noop, setDisplayMode = noop })=>{
    const { t } = useTranslation();
    const isSubscriptionPurchaseFTUEBeingShown = !!useContext(SubscriptionPurchaseFTUEContext);
    const isDuperbrowseActive = useSelector(getIsDuperbrowseActive);
    const { isFullscreen } = useDeviationViewContext();
    const viewportRef = useRef(null);
    const { contentSize = {} } = useMeasuredStage(viewportRef, !isFullscreen, MeasuredCookieType.DEVIATION_MULTI_IMAGE_SCROLL);
    // if there's a currentlyViewedImageSource, scroll to it
    const isScrolling = useScrollToCurrentlyViewed(deviation, additionalMedia, currentlyViewedImageSource);
    var _zoomedImageSource_isPublished;
    return /*#__PURE__*/ React.createElement(StageContainer, {
        className: classnames(className, styles['root'], styles['scroll-mode'], isFullscreen && styles['fullscreen']),
        withoutInnerWrapper: true
    }, /*#__PURE__*/ React.createElement(BiLoggerContextProvider, {
        value: BI_SCROLL_SECTIONNAME
    }, /*#__PURE__*/ React.createElement("div", {
        className: styles['viewport-measurer']
    }, /*#__PURE__*/ React.createElement("div", {
        ref: viewportRef
    }), /*#__PURE__*/ React.createElement(IconButton, {
        className: styles['switch-to-carousel-button'],
        icon: CarouselIcon,
        size: "large",
        tooltipText: t('deviationview.artstage.changeToCarouselMode.tooltip'),
        tooltipOffset: [
            0,
            14
        ],
        "aria-label": t('deviationview.artstage.changeToCarouselMode.altLabel'),
        onClick: ()=>{
            setDisplayMode('carousel');
            window.scrollTo(0, 0);
        },
        biData: BiData({
            evid: 309,
            deviationid: deviation.deviationId,
            view_mode: 'carousel'
        })
    }), (deviation.isPublished || isDuperbrowseActive) && /*#__PURE__*/ React.createElement(DuperbrowsePaddles, {
        className: styles['duperbrowse'],
        isInZoomModal: isZoomed
    }), !deviation.isPublished && !isDuperbrowseActive && /*#__PURE__*/ React.createElement(StashPaddles, null)), /*#__PURE__*/ React.createElement("div", {
        className: styles['content']
    }, /*#__PURE__*/ React.createElement(EmptyBackground, null), /*#__PURE__*/ React.createElement("ol", {
        className: classnames(styles['scroll-mode-list'], isScrolling && styles['scroll-mode-list-scrolling'])
    }, /*#__PURE__*/ React.createElement(InView, {
        key: "original-deviation",
        as: "li",
        threshold: SCROLL_MODE_THRESHOLD_FOR_INVIEW,
        onChange: (inView)=>{
            inView && !isScrolling && setCurrentlyViewedImageSource(deviation);
        }
    }, /*#__PURE__*/ React.createElement(FullImageSourceWithCaption, _object_spread_props(_object_spread({
        id: "deviation-image",
        deviationOrAttachment: deviation,
        withoutZoomModal: true
    }, contentSize), {
        isZoomed: isZoomed,
        onEnterZoomMode: onEnterZoomMode,
        onExitZoomMode: onExitZoomMode
    }))), additionalMedia.map((image, i)=>{
        return /*#__PURE__*/ React.createElement(InView, {
            key: `additional-media-${image.filename}`,
            as: "li",
            threshold: SCROLL_MODE_THRESHOLD_FOR_INVIEW,
            onChange: (inView)=>{
                inView && !isScrolling && setCurrentlyViewedImageSource(image);
            }
        }, /*#__PURE__*/ React.createElement(FullImageSourceWithCaption, _object_spread_props(_object_spread({
            id: `${ADDITIONAL_MEDIA_ID_PREFIX}${i}`,
            deviationOrAttachment: image,
            dontExpandWrapper: true,
            withoutZoomModal: true
        }, contentSize), {
            isZoomed: isZoomed,
            onEnterZoomMode: onEnterZoomMode,
            onExitZoomMode: onExitZoomMode
        })));
    }))), zoomedImageSource && /*#__PURE__*/ React.createElement(ZoomModalFromImageSource, _object_spread_props(_object_spread({
        source: zoomedImageSource,
        allowFullviewSrcsetForPngs: (_zoomedImageSource_isPublished = zoomedImageSource === null || zoomedImageSource === void 0 ? void 0 : zoomedImageSource.isPublished) !== null && _zoomedImageSource_isPublished !== void 0 ? _zoomedImageSource_isPublished : false
    }, contentSize), {
        isZoomed: isZoomed,
        onExitZoomMode: onExitZoomMode
    })), isSubscriptionPurchaseFTUEBeingShown && /*#__PURE__*/ React.createElement(SubscriptionPurchaseFTUE, {
        className: styles['subscription-purchase-ftue']
    })));
};
DesktopMultiArtScrollStage.displayName = 'DesktopMultiArtScrollStage';
export default DesktopMultiArtScrollStage;

//# sourceMappingURL=DesktopMultiArtScrollStage.js.map